import type { CollectionReference, DocumentData } from 'firebase/firestore'
import {
  collection,
  doc,
  runTransaction,
  serverTimestamp,
  type Firestore,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { modelItemStream, modelListStream } from '../../firestore-mobx/stream'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import { PromptRubricType } from '../../models/PromptRubricType'
import { buildUnsupportedConverter } from '../shared/noops'
import type { FirestorePromptRubricType } from './schema'
import { schema } from './schema'
import { RubricType } from './types'
import { updateDocWithError } from '../../firestore-mobx/fetch'

export { RubricType }

export * from './schema'

const unsupported = buildUnsupportedConverter<FirestorePromptRubricType>()

const readConverter: FirestoreDataConverter<FirestorePromptRubricType> = {
  toFirestore: unsupported.toFirestore,
  fromFirestore: (snapshot: QueryDocumentSnapshot) => {
    const data = snapshot.data({ serverTimestamps: 'estimate' })
    return schema.parse(data)
  },
}

const getReadCollectionRef = (
  firestore: Firestore
): CollectionReference<FirestorePromptRubricType, DocumentData> => {
  return collection(firestore, 'prompt', 'rubric_prompt', 'type').withConverter(
    readConverter
  )
}

export const getRoomPromptTypes = (repository: FirebaseRepository) => {
  const ref = getReadCollectionRef(repository.firestore)
  return modelListStream(repository, ref, PromptRubricType)
}

export const getRoomPromptType = (
  repository: FirebaseRepository,
  { id }: { id: string }
) => {
  const ref = getReadCollectionRef(repository.firestore)
  return modelItemStream(repository, doc(ref, id), PromptRubricType)
}

export const updatePromptRubricType = async (
  repository: FirebaseRepository,
  {
    title,
    description,
    rubricType,
  }: Pick<FirestorePromptRubricType, 'title' | 'description'> & {
    rubricType: RubricType
  }
) => {
  const ref = getReadCollectionRef(repository.firestore)
  const docRef = doc(ref, rubricType.toString()).withConverter(null)
  return await updateDocWithError(
    docRef,
    {
      title,
      description,
      updatedAt: serverTimestamp(),
    },
    'UpdatePromptRubricTypeError'
  )
}

export const updatePromptRubricTypeScoreDescription = async (
  repository: FirebaseRepository,
  {
    rubricType,
    scoreIndex,
    scoreDescription,
  }: {
    rubricType: RubricType
    scoreIndex: number
    scoreDescription: Omit<
      FirestorePromptRubricType['scoreDescriptions'][number],
      'score' | 'color'
    >
  }
) => {
  await runTransaction(repository.firestore, async (transaction) => {
    // get the rubric prompt type doc
    const ref = getReadCollectionRef(repository.firestore)
    const docRef = doc(ref, rubricType.toString())

    const docSnap = await transaction.get(docRef)

    if (!docSnap.exists())
      throw new Error(`Rubric type ${rubricType} does not exist`)

    if (!docSnap.data()?.scoreDescriptions)
      throw new Error(
        `Rubric type ${rubricType} does not have score descriptions`
      )

    const scoreDescriptions = docSnap.data().scoreDescriptions

    if (scoreIndex < 0 || scoreIndex >= scoreDescriptions.length)
      throw new Error(
        `Score index ${scoreIndex} is out of bounds for rubric type ${rubricType}`
      )

    // edit the selected description in place
    const updatedScoreDescriptions = scoreDescriptions.map((desc, index) => {
      if (index === scoreIndex) {
        return {
          ...desc,
          ...scoreDescription,
        }
      }
      return desc
    })

    // update the doc with the new score descriptions
    transaction.update(docRef, {
      scoreDescriptions: updatedScoreDescriptions,
      updatedAt: serverTimestamp(),
    })
  })
}

export const updatePromptRubricTypeVariant = async (
  repository: FirebaseRepository,
  {
    rubricType,
    variantKey,
    variant,
  }: {
    rubricType: RubricType
    variantKey: string
    variant: Pick<
      FirestorePromptRubricType['variants'][number],
      'title' | 'description' | 'rubricPrompt'
    >
  }
) => {
  await runTransaction(repository.firestore, async (transaction) => {
    // get the rubric prompt type doc
    const ref = getReadCollectionRef(repository.firestore)
    const docRef = doc(ref, rubricType.toString())

    const docSnap = await transaction.get(docRef)

    if (!docSnap.exists())
      throw new Error(`Rubric type ${rubricType} does not exist`)

    const variants = docSnap.data()?.variants

    if (!variants) throw new Error(`Rubric type ${rubricType} has no variants`)

    // find the variant by its stable variantKey (the stored array order may
    // differ from the sorted view the model exposes)
    if (!variants.some((v) => v.variantKey === variantKey))
      throw new Error(
        `Variant ${variantKey} not found for rubric type ${rubricType}`
      )

    // edit the selected variant in place, preserving variantKey + order
    const updatedVariants = variants.map((v) =>
      v.variantKey === variantKey ? { ...v, ...variant } : v
    )

    // update the doc with the new variants
    transaction.update(docRef, {
      variants: updatedVariants,
      updatedAt: serverTimestamp(),
    })
  })
}
